import React, { useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;

// Colores por tipo de institución
const COLORES_TIPO = {
  'Salud': '#EF4444',
  'Educación': '#3B82F6',
  'Desarrollo Social': '#10B981',
  'Justicia': '#8B5CF6',
  'Seguridad': '#F59E0B',
  'ONG': '#EC4899',
  'default': '#6B7280',
};

// Centros de municipios (compat con la versión Leaflet)
const CENTROS_MUNICIPIOS = {
  'Lomas de Zamora': { lat: -34.7608, lng: -58.4092 },
  'Almirante Brown': { lat: -34.8177, lng: -58.3967 },
  'Avellaneda': { lat: -34.6621, lng: -58.3669 },
  'Lanús': { lat: -34.7060, lng: -58.3925 },
  'Esteban Echeverría': { lat: -34.8167, lng: -58.4667 },
  'Ezeiza': { lat: -34.8500, lng: -58.5167 },
};

const DEFAULT_CENTER = { lat: -34.7608, lng: -58.4092 };

// Alfiler SVG inline (equivalente a `createColoredIcon` de Leaflet).
function MarkerPin({ color }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="28" height="42"
      style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.35))' }}>
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"
        fill={color} stroke="white" strokeWidth="1.2" />
      <circle cx="12" cy="12" r="5" fill="white" />
    </svg>
  );
}

// Pin para el usuario (círculo azul).
function UserPin() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#3B82F6" stroke="white" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" fill="white" />
    </svg>
  );
}

// Círculo de radio: usamos google.maps.Circle directo (no hay wrapper en vis.gl).
function RadiusCircle({ center, radiusMeters }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || !center) return;
    const circle = new window.google.maps.Circle({
      map,
      center,
      radius: radiusMeters,
      strokeColor: '#3B82F6',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: '#3B82F6',
      fillOpacity: 0.08,
    });
    return () => circle.setMap(null);
  }, [map, center, radiusMeters]);
  return null;
}

// Centrar el mapa cuando cambia userLocation / centrarEnInstitucion.
function CenterController({ userLocation, centrarEnInstitucion }) {
  const map = useMap();
  // Centrar en ubicación del usuario (primera vez / cada cambio)
  useEffect(() => {
    if (!map || !userLocation) return;
    map.panTo({ lat: userLocation.lat, lng: userLocation.lng });
    map.setZoom(14);
  }, [map, userLocation]);
  // Centrar en la institución seleccionada desde la lista
  useEffect(() => {
    if (!map || !centrarEnInstitucion?.coordenadas) return;
    const c = centrarEnInstitucion.coordenadas;
    const lat = c.lat;
    const lng = c.lng ?? c.lon;
    if (lat != null && lng != null) {
      map.panTo({ lat, lng });
      map.setZoom(16);
    }
  }, [map, centrarEnInstitucion]);
  return null;
}

// Click handler (para selector de ubicación).
function ClickHandler({ onMapClick }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !onMapClick) return;
    const listener = map.addListener('click', (e) => {
      const latLng = e.latLng;
      if (!latLng) return;
      onMapClick({ lat: latLng.lat(), lng: latLng.lng() });
    });
    return () => window.google?.maps?.event?.removeListener(listener);
  }, [map, onMapClick]);
  return null;
}

export default function RecurseroMapa({
  instituciones = [],
  userLocation = null,
  selectedInstitucion = null,
  onInstitucionClick = null,
  onMapClick = null,
  showUserLocation = true,
  radioKm = 5,
  height = '500px',
  interactive = true,
  selectionMode = false,
  municipio = null,
  centroInicial = null,
  zoomInicial = null,
  centrarEnInstitucion = null,
}) {
  const [openInfoId, setOpenInfoId] = useState(null);

  // Determinar centro según prioridad: centroInicial > userLocation > municipio > default
  const center = useMemo(() => {
    if (centroInicial) return centroInicial;
    if (userLocation) return userLocation;
    if (municipio && CENTROS_MUNICIPIOS[municipio]) return CENTROS_MUNICIPIOS[municipio];
    return DEFAULT_CENTER;
  }, [centroInicial, userLocation, municipio]);

  const zoom = zoomInicial || (userLocation ? 14 : 12);

  const colorPorInstitucion = (inst) => {
    if (inst.etiquetas_info && inst.etiquetas_info.length > 0) {
      return inst.etiquetas_info[0].color;
    }
    return COLORES_TIPO[inst.tipo_institucion] || COLORES_TIPO.default;
  };

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div
        style={{ height, width: '100%' }}
        className="rounded-lg border bg-gray-50 flex items-center justify-center text-sm text-gray-500 p-4 text-center"
      >
        Falta configurar <code className="mx-1 bg-gray-100 px-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code> en el entorno.
      </div>
    );
  }

  return (
    <div style={{ height, width: '100%' }} className="rounded-lg overflow-hidden border" data-testid="recursero-mapa">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['marker']}>
        <Map
          defaultCenter={center}
          defaultZoom={zoom}
          mapId={GOOGLE_MAP_ID}
          style={{ width: '100%', height: '100%' }}
          gestureHandling={interactive ? 'greedy' : 'none'}
          disableDefaultUI={!interactive}
          zoomControl={interactive}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          clickableIcons={false}
        >
          {/* Centrador diferido */}
          <CenterController userLocation={userLocation} centrarEnInstitucion={centrarEnInstitucion} />

          {/* Click handler para selector */}
          {onMapClick && <ClickHandler onMapClick={onMapClick} />}

          {/* Círculo de radio de búsqueda */}
          {userLocation && radioKm > 0 && (
            <RadiusCircle center={userLocation} radiusMeters={radioKm * 1000} />
          )}

          {/* Marcador del usuario */}
          {showUserLocation && userLocation && (
            <AdvancedMarker position={userLocation} title="Tu ubicación">
              <UserPin />
            </AdvancedMarker>
          )}

          {/* Marcadores de instituciones */}
          {instituciones.map((inst) => {
            const coords = inst.coordenadas;
            if (!coords) return null;
            const lat = coords.lat;
            const lng = coords.lng ?? coords.lon;
            if (lat == null || lng == null) return null;
            const color = colorPorInstitucion(inst);
            return (
              <AdvancedMarker
                key={inst.id}
                position={{ lat, lng }}
                onClick={() => setOpenInfoId(inst.id)}
                title={inst.nombre_organismo || inst.nombre}
              >
                <MarkerPin color={color} />
                {openInfoId === inst.id && (
                  <InfoWindow
                    position={{ lat, lng }}
                    onCloseClick={() => setOpenInfoId(null)}
                    pixelOffset={[0, -40]}
                  >
                    <div className="max-w-xs">
                      <h3 className="font-bold text-sm mb-1">{inst.nombre_organismo || inst.nombre}</h3>
                      <div className="text-xs text-gray-600 mb-2 space-y-0.5">
                        <p className="flex items-center gap-1">
                          📍 {inst.municipio}{inst.localidad && `, ${inst.localidad}`}
                        </p>
                        {inst.direccion_texto && <p className="text-gray-500">{inst.direccion_texto}</p>}
                        {inst.domicilio && typeof inst.domicilio === 'object' && inst.domicilio.calle && (
                          <p className="text-gray-500">
                            {inst.domicilio.calle} {inst.domicilio.numero}
                          </p>
                        )}
                        {inst.distancia_km !== undefined && (
                          <p className="font-medium text-blue-600">A {inst.distancia_km} km de tu ubicación</p>
                        )}
                      </div>
                      {inst.telefonos && inst.telefonos.length > 0 && (
                        <a
                          href={`tel:${inst.telefonos[0]}`}
                          className="flex items-center gap-1 text-xs text-blue-600 font-medium mb-2 hover:underline"
                        >
                          📞 {inst.telefonos[0]}
                        </a>
                      )}
                      {inst.etiquetas_info && inst.etiquetas_info.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {inst.etiquetas_info.map((etq) => (
                            <span
                              key={etq.id}
                              className="text-xs px-1.5 py-0.5 rounded-full text-white"
                              style={{ backgroundColor: etq.color }}
                            >
                              {etq.nombre}
                            </span>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onInstitucionClick && onInstitucionClick(inst);
                        }}
                        className="w-full mt-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors"
                        data-testid={`btn-info-${inst.id}`}
                      >
                        Más información
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </AdvancedMarker>
            );
          })}
        </Map>
      </APIProvider>
    </div>
  );
}

// Selector simplificado (mismo API público que la versión Leaflet).
export function MapaSelector({ value = null, onChange, height = '300px' }) {
  const [position, setPosition] = useState(value);
  const handleMapClick = (coords) => {
    setPosition(coords);
    onChange && onChange(coords);
  };
  return (
    <div>
      <RecurseroMapa
        userLocation={position}
        onMapClick={handleMapClick}
        showUserLocation={true}
        radioKm={0}
        height={height}
        instituciones={[]}
      />
      {position && (
        <p className="text-xs text-gray-500 mt-2">
          Coordenadas: {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
        </p>
      )}
    </div>
  );
}
